import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { SourceAuthorityType } from '../../../core/data-profiles/sourceAuthorityTypes';
import type {
  FolderSourceAuthority,
  FolderSourceAuthorityOverview,
  SourceAuthorityKind,
  SourceAuthorityTransferReason,
  SourceAuthorityTransferReview,
} from '../models/FolderSourceAuthority';
import { folderSourceAuthorityKeys } from '../../../core/data-profiles/folderSourceAuthorityQueryKeys';

export { folderSourceAuthorityKeys } from '../../../core/data-profiles/folderSourceAuthorityQueryKeys';

export function useFolderSourceAuthorities(
  folderId: string | undefined,
  enabled: boolean,
) {
  const authenticatedFetch = useAuthenticatedFetch();
  const query = useQuery<FolderSourceAuthorityOverview>({
    queryKey: folderSourceAuthorityKeys.overview(folderId),
    enabled: enabled && Boolean(folderId),
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId ?? '')}/source-authorities`,
      );
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to load primary sources'));
      }
      return response.json() as Promise<FolderSourceAuthorityOverview>;
    },
    staleTime: 30_000,
  });

  return {
    authorities: query.data?.authorities ?? [],
    suggestions: query.data?.suggestions ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useSetFolderSourceAuthority(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<
    { authority: FolderSourceAuthority },
    Error,
    {
      sourceType: SourceAuthorityType;
      authorityKind: SourceAuthorityKind;
      bindingId?: string | null;
    }
  >({
    mutationFn: async ({ sourceType, authorityKind, bindingId }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-authorities/${encodeURIComponent(sourceType)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ authorityKind, bindingId }),
        },
      );
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to update primary source'));
      }
      return response.json() as Promise<{ authority: FolderSourceAuthority }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderSourceAuthorityKeys.overview(folderId) });
    },
  });
}

export function useReviewFolderSourceAuthorityTransfer(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation<
    { review: SourceAuthorityTransferReview },
    Error,
    {
      sourceType: SourceAuthorityType;
      authorityKind: SourceAuthorityKind;
      bindingId?: string | null;
    }
  >({
    mutationFn: async ({ sourceType, authorityKind, bindingId }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        authorityUrl(folderId, sourceType, 'review-transfer'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ authorityKind, bindingId }),
        },
      );
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to review authority change'));
      }
      return response.json() as Promise<{ review: SourceAuthorityTransferReview }>;
    },
  });
}

export function useExecuteFolderSourceAuthorityTransfer(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation<
    { authority: FolderSourceAuthority },
    Error,
    {
      review: SourceAuthorityTransferReview;
      reason: SourceAuthorityTransferReason;
    }
  >({
    mutationFn: async ({ review, reason }) => {
      if (!folderId) throw new Error('Folder id is required');
      const isAdoption = review.target.authorityKind === 'local';
      const response = await authenticatedFetch(
        authorityUrl(folderId, review.sourceType, isAdoption ? 'adopt' : 'transfer'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(!isAdoption ? { bindingId: review.target.bindingId } : {}),
            reviewedCurrentUpdatedAt: review.reviewedCurrentUpdatedAt,
            ...(!isAdoption ? { reviewedTargetBaseRunId: review.target.baseRunId } : {}),
            reason,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to apply authority change'));
      }
      return response.json() as Promise<{ authority: FolderSourceAuthority }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderSourceAuthorityKeys.overview(folderId) });
    },
  });
}

export function useReverseFolderSourceAuthorityTransfer(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  return useMutation<
    { authority: FolderSourceAuthority },
    Error,
    { sourceType: SourceAuthorityType; reviewedCurrentUpdatedAt: string }
  >({
    mutationFn: async ({ sourceType, reviewedCurrentUpdatedAt }) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(authorityUrl(folderId, sourceType, 'reverse'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewedCurrentUpdatedAt }),
      });
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to reverse authority change'));
      }
      return response.json() as Promise<{ authority: FolderSourceAuthority }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderSourceAuthorityKeys.overview(folderId) });
    },
  });
}

export function useClearFolderSourceAuthority(folderId: string | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<{ ok: true }, Error, SourceAuthorityType>({
    mutationFn: async (sourceType) => {
      if (!folderId) throw new Error('Folder id is required');
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-authorities/${encodeURIComponent(sourceType)}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        throw new Error(await sourceAuthorityError(response, 'Failed to clear primary source'));
      }
      return response.json() as Promise<{ ok: true }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderSourceAuthorityKeys.overview(folderId) });
    },
  });
}

async function sourceAuthorityError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as { error?: string } | null;
  return body?.error ? `${fallback}: ${body.error}` : `${fallback}: ${response.status}`;
}

function authorityUrl(
  folderId: string,
  sourceType: SourceAuthorityType,
  action: 'review-transfer' | 'transfer' | 'adopt' | 'reverse',
): string {
  return `/api/folders/${encodeURIComponent(folderId)}/source-authorities/${encodeURIComponent(sourceType)}/${action}`;
}
